import { Drive, DriveAccount, JFileApi, JFilebankApi, jFileLittle, MSDocumentEntity, Network } from 'jamespot-user-api';
import { JDriveApi } from 'jamespot-user-api/lib/src/apis/drive/drive';
import * as React from 'react';
import { MouseEventHandler, useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { WrappedFieldInputProps } from 'redux-form';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import { DriveService } from '../../../Drives/drive.types';
import { JRCDriveDropbox } from '../../../Drives/JRCDriveDropbox';
import { JRCDriveMSGraph } from '../../../Drives/JRCDriveMSGraph';
import { JRCAttachments } from '../../../JRCAttachments/JRCAttachments';
import { JRCFolders } from '../../../JRCFolders/JRCFolders';
import { JRCLoader } from '../../../JRCLoader/JRCLoader';
import { JRCMessage } from '../../../JRCMessage/JRCMessage';
import { JRCModalContent } from '../../../JRCModal/JRCModalContent';
import { JRCModalLayout } from '../../../JRCModal/JRCModalLayout';
import { JRCTabs } from '../../../JRCTabs/JRCTabs';
import { JRCText } from '../../../JRCTypo/JRCTypo';
import { File } from '../Deprecated/JRCFormFile/components/File';
import { fileSend } from '../JRCInputFile/utils/file.api';

const LabelContainer = styled.label`
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    border-radius: 8px;
    background-color: ${(props) => props.theme.color.white};
    width: 100%;
    height: 56px;
    border: dashed 1px ${(props) => props.theme.color.grey2};
    cursor: pointer;

    :hover {
        border: solid 1px ${(props) => props.theme.color.grey2};
        background-color: ${(props) => props.theme.color.grey0};
    }

    &.dropHover {
        border: dashed 2px ${(props) => props.theme.color.navy};
        background-color: ${(props) => props.theme.color.grey0};
    }
`;

const FileUploadWrapper = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;

    input {
        position: absolute;
        z-index: 0;
    }
`;
export type FileWithId = File & {
    id?: string;
};

type initialUploaderState = {
    files: FileWithId[];
    loading: boolean;
    hovered: boolean;
    error: boolean;
    success: boolean;
};

export type DriveOrFilebank = Drive | 'fileBank';
type FileTab = DriveOrFilebank | 'upload';

const initialUploader: initialUploaderState = {
    files: [],
    loading: false,
    hovered: false,
    error: false,
    success: false,
};

export interface JRCInputFileAdavancedProps extends React.ComponentPropsWithoutRef<'input'> {
    required?: boolean;
    loading?: boolean;
    className?: string;
    content?: string;
    icon?: boolean;
    limit?: number;
    token?: string;
    multiple?: boolean;
    accept?:
        | 'image/png'
        | 'image/jpg'
        | 'image/png, image/jpg'
        | 'image/png, image/jpeg, image/jpg'
        | 'image/pdf'
        | 'image/doc'
        | 'image/pdf, image/doc'
        | 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.spreadsheet,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.oasis.opendocument.text,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.oasis.opendocument.presentation';
    input?: WrappedFieldInputProps;
    onUploadSuccess?: (response: jFileLittle, token?: string) => void;
    network?: Pick<Network, 'token' | 'postFile' | 'getUrl'>;
    onDeleteSuccess?: (index: number) => void;
    onUploadStart?: (count?: number) => void;
    existingFiles?: Array<jFileLittle>;
    deleteFile?: (id: number) => Promise<void>;
    activeDrives?: DriveOrFilebank[];
    dispatchOnError?: (code?: string) => void;
    handlers?: {
        filebank: Pick<JFilebankApi, 'getBanks' | 'getFolders' | 'getDocuments'>;
        file: Pick<JFileApi, 'copy'>;
        drive: Pick<
            JDriveApi,
            | 'msDocuments'
            | 'dropboxDocuments'
            | 'groups'
            | 'tenants'
            | 'listAccount'
            | 'isAccountLogged'
            | 'checkAccountToken'
            | 'getAccount'
            | 'logout'
            | 'setDefaultAccount'
            | 'getDocument'
        >;
    };
    directFilePopupOpening?: { open: boolean; onClose: () => void };
}

const ExistingFilesLabelContainer = styled.div`
    margin: 12px 0 8px 0;
`;

const ExistingFilesContainer = styled.div``;

const UploadedFilesWrapper = styled.div`
    margin-top: 8px;
`;

const TabsWrapper = styled.div`
    position: sticky;
    top: -${({ theme }) => theme.space.l}px;
    margin-top: -${({ theme }) => theme.space.l}px;
    background: ${({ theme }) => theme.color.white};
    z-index: 2;
    padding-top: ${({ theme }) => theme.space.md}px;
`;

const InputFileAdvancedRaw = (props: JRCInputFileAdavancedProps, ref: React.ForwardedRef<HTMLInputElement>) => {
    const {
        id = 'file',
        name = 'attachment',
        content,
        token = undefined,
        accept,
        multiple = false,
        limit = 10,
        required = false,
        network,
        onUploadSuccess,
        onUploadStart,
        onDeleteSuccess,
        existingFiles,
        deleteFile,
    } = props;
    const intl = useIntl();
    const [step, setStep] = React.useState(0);
    const [loading] = React.useState<boolean>(false);
    const [hovered, setHovered] = React.useState<boolean>(false);
    const [uploader, setUploader] = React.useState<initialUploaderState>(initialUploader);
    const [existingFilesState, setExistingFilesState] = React.useState<Array<jFileLittle & { loading: boolean }>>(
        existingFiles?.map((f) => ({ ...f, loading: false })) || [],
    );
    const [isFilePopupOpen, setFilePopupOpen] = useState(!!props.directFilePopupOpening);
    const [activeFileTabItem, setActiveFileTabItem] = useState<FileTab>('upload');
    const [files, setFiles] = useState<jFileLittle[]>([]);
    const [isFilePopupLoading, setFilePopupLoading] = useState(false);
    const [driveFiles, setDriveFiles] = useState<MSDocumentEntity[]>([]);
    const [loggedAccount, setLoggedAccount] = useState<DriveAccount>();

    const tabsName = useMemo<Record<FileTab, string>>(
        () => ({
            fileBank: intl.formatMessage({ id: 'INPUT_FILE_ADVANCED_FileBank' }),
            drive_microsoft_graph: intl.formatMessage({ id: 'INPUT_FILE_ADVANCED_Microsoft_365' }),
            drive_dropbox: intl.formatMessage({ id: 'INPUT_FILE_ADVANCED_Dropbox' }),
            drive_google: intl.formatMessage({ id: 'INPUT_FILE_ADVANCED_Google_Drive' }),
            upload: intl.formatMessage({ id: 'GLOBAL_Import' }),
        }),
        [intl],
    );

    const handleChange = useCallback(
        (event: React.ChangeEvent<HTMLInputElement>) => {
            const target = event.target as HTMLInputElement;
            const fileList = target.files as FileList;
            const files: FileWithId[] = [];
            for (let i = 0; i < fileList.length; i++) {
                files.push(Object.assign(fileList[i], { id: uuidv4() }));
            }

            if (multiple && limit !== 1) {
                setUploader({ ...uploader, files: multiple ? [...uploader.files, ...files] : files });
                if (onUploadStart) {
                    onUploadStart(files.length);
                }
            } else {
                if (network && 'getUrl' in network && 'token' in network && 'postFile' in network) {
                    const body = new FormData();

                    body.append('attrName', name);
                    body.append(name, files[0]);
                    if (token !== undefined) {
                        body.append('token', token);
                    }

                    network.postFile(body).then((res) => {
                        if (onUploadSuccess) {
                            onUploadSuccess(res.response.result, res.token);
                        }
                    });
                } else {
                    fileSend(files[0], token, name).then((res) => {
                        if (onUploadSuccess) {
                            onUploadSuccess(res.result, res.token);
                        }
                    });
                }
            }
        },
        [limit, multiple, name, onUploadStart, onUploadSuccess, token, uploader, network],
    );

    const deleteExistingFile = useCallback(
        (id: number) => {
            return new Promise<void>((resolve, reject) => {
                if (deleteFile) {
                    setExistingFilesState(existingFilesState?.map((f) => (f.id === id ? { ...f, loading: true } : f)));
                    deleteFile(id)
                        .then(() => {
                            setExistingFilesState(existingFilesState?.filter((f) => f.id !== id));
                            resolve();
                        })
                        .catch(() => {
                            setExistingFilesState(
                                existingFilesState?.map((f) =>
                                    f.id === id
                                        ? {
                                              ...f,
                                              loading: false,
                                          }
                                        : f,
                                ),
                            );
                            reject();
                        });
                }
            });
        },
        [deleteFile, existingFilesState],
    );

    const allowDrop = useCallback((event: any) => {
        setHovered(true);
        event.preventDefault();
    }, []);

    const drop = useCallback(
        (event: any) => {
            event.preventDefault();

            const fileList = event.dataTransfer.files as FileList;
            const files: File[] = [];
            for (let i = 0; i < fileList.length; i++) {
                files.push(fileList[i]);
            }

            if (multiple && limit !== 1) {
                setUploader({ ...uploader, files: multiple ? [...uploader.files, ...files] : files });
                if (onUploadStart) {
                    onUploadStart(files.length);
                }
            } else {
                if (network && 'getUrl' in network && 'token' in network && 'postFile' in network) {
                    const body = new FormData();

                    body.append('attrName', name);
                    body.append(name, files[0]);
                    if (token !== undefined) {
                        body.append('token', token);
                    }
                    network.postFile(body).then((res) => {
                        if (onUploadSuccess) {
                            onUploadSuccess(res.response.result, res.token);
                        }
                    });
                } else {
                    fileSend(files[0], token, name).then((res) => {
                        if (onUploadSuccess) {
                            onUploadSuccess(res.result, res.token);
                        }
                    });
                }
                if (onUploadStart) {
                    onUploadStart();
                }
            }
        },
        [limit, multiple, name, onUploadStart, onUploadSuccess, token, uploader, network],
    );

    const onClickInput: MouseEventHandler<HTMLLabelElement> = useCallback((e) => {
        e.stopPropagation();
        setFilePopupOpen(true);
    }, []);

    const deleteFiles = useCallback(() => {
        files.forEach((f) => {
            if (deleteFile) {
                deleteFile(f.id).then(() => {
                    setFiles([]);
                });
            }
        });
        setDriveFiles([]);
    }, [deleteFile, files]);

    const cancelFileUpload = useCallback(() => {
        if (props.directFilePopupOpening) {
            props.directFilePopupOpening.onClose();
        } else {
            setFilePopupOpen(false);
        }
        deleteFiles();
    }, [deleteFiles, props.directFilePopupOpening]);

    const reinitModalAfterUpload = useCallback(() => {
        if (props.directFilePopupOpening) {
            props.directFilePopupOpening.onClose();
        } else {
            setFilePopupOpen(false);
        }
        setFiles([]);
        setDriveFiles([]);
        setFilePopupLoading(false);
    }, [props.directFilePopupOpening]);

    const service: DriveService = useMemo(
        () => activeFileTabItem.replace(/(drive_|_)/g, '') as DriveService,
        [activeFileTabItem],
    );

    const uploadFiles = useCallback(() => {
        if (files.length > 0 && props.handlers?.file.copy) {
            setFilePopupLoading(true);
            Promise.all(
                files.map(async (f) => {
                    let processedFile = f;
                    if (f.type === 'fileArticle' && props.handlers?.file.copy) {
                        processedFile = (
                            await props.handlers.file.copy({
                                id: f.id,
                                token,
                                attrName: 'attachment',
                            })
                        ).result;
                    }
                    return {
                        ...processedFile,
                        loading: false,
                    };
                }),
            ).then((processedFiles) => {
                setExistingFilesState([...existingFilesState, ...processedFiles]);
                if (onUploadSuccess) {
                    processedFiles.forEach((f) => onUploadSuccess(f));
                }
                reinitModalAfterUpload();
            });
        } else if (driveFiles.length > 0 && loggedAccount && props.handlers?.drive.getDocument) {
            setFilePopupLoading(true);
            Promise.all(
                driveFiles.map(
                    async (f) =>
                        (
                            await props.handlers?.drive.getDocument({
                                token,
                                attrName: 'attachment',
                                file: {
                                    mimetype: f.mimetype,
                                    service,
                                    account: loggedAccount.id,
                                    title: f.title,
                                    _document: f,
                                },
                            })
                        )?.result ??
                        ({
                            id: 0,
                            mimetype: '',
                            title: '',
                            type: '',
                            loading: true,
                        } as (typeof existingFilesState)[number]),
                ),
            ).then((processedFiles) => {
                setExistingFilesState([
                    ...existingFilesState,
                    ...processedFiles.map((f) => ({
                        ...f,
                        loading: false,
                    })),
                ]);
                if (onUploadSuccess) {
                    processedFiles.forEach((f) => onUploadSuccess(f));
                }
                reinitModalAfterUpload();
            });
        }
    }, [
        files,
        props.handlers,
        driveFiles,
        loggedAccount,
        token,
        existingFilesState,
        onUploadSuccess,
        reinitModalAfterUpload,
        service,
    ]);

    useEffect(() => {
        setFilePopupOpen(props.directFilePopupOpening?.open ?? false);
    }, [props.directFilePopupOpening]);

    return (
        <>
            {loading ? (
                <LabelContainer>
                    <JRCLoader />
                </LabelContainer>
            ) : (
                <>
                    <FileUploadWrapper>
                        <input
                            ref={ref}
                            id={id}
                            type={props.directFilePopupOpening ? 'hidden' : 'file'}
                            required={required}
                            name={name}
                            multiple={multiple}
                            accept={accept}
                            onChange={handleChange}
                            disabled={props.activeDrives && props.activeDrives.length > 0}
                        />
                        {!props.directFilePopupOpening && (
                            <LabelContainer
                                className={hovered !== null && hovered ? 'dropHover' : 'initHover'}
                                htmlFor={!props.activeDrives || props.activeDrives.length === 0 ? id : undefined}
                                onClick={props.activeDrives && props.activeDrives.length > 0 ? onClickInput : undefined}
                                onMouseLeave={() => setHovered(false)}
                                onDragLeave={() => setHovered(false)}
                                onDrop={drop}
                                onDragOver={allowDrop}>
                                {content ? content : intl.formatMessage({ id: 'FILE_Upload_Label' })}
                            </LabelContainer>
                        )}
                    </FileUploadWrapper>
                    {multiple && limit !== 1 ? (
                        <UploadedFilesWrapper>
                            {uploader.files.map((file, index: number) => (
                                <File
                                    file={file}
                                    index={index}
                                    token={token}
                                    attrName={name}
                                    key={file.id}
                                    step={step}
                                    onUploadSuccess={(response, uploadToken) => {
                                        setStep((prevStep) => prevStep + 1);
                                        if (onUploadSuccess) {
                                            onUploadSuccess(response, uploadToken);
                                        }
                                    }}
                                    onDeleteSuccess={() => {
                                        setUploader((prevUploader) => ({
                                            ...prevUploader,
                                            files: prevUploader.files.filter((_, i) => i !== index),
                                        }));
                                        if (onDeleteSuccess) {
                                            onDeleteSuccess(index);
                                        }
                                    }}
                                />
                            ))}
                        </UploadedFilesWrapper>
                    ) : (
                        <></>
                    )}
                </>
            )}
            {existingFilesState && existingFilesState.length !== 0 && (
                <>
                    <ExistingFilesLabelContainer>
                        <JRCText size="s">{intl.formatMessage({ id: 'FILE_Existing_Files_Label' })}</JRCText>
                    </ExistingFilesLabelContainer>
                    <ExistingFilesContainer>
                        <JRCAttachments
                            files={existingFilesState}
                            onDelete={(id) => deleteExistingFile(id)}
                            mode={'edit'}
                        />
                    </ExistingFilesContainer>
                </>
            )}
            {((props.activeDrives && props.activeDrives.length > 0) || props.directFilePopupOpening) && (
                <JRCModalLayout open={isFilePopupOpen} closeHandler={cancelFileUpload} variant={'fixed-height'}>
                    <JRCModalContent
                        overflow={'scroll'}
                        onClickIconClose={cancelFileUpload}
                        title={intl.formatMessage({ id: 'GLOBAL_Files' })}
                        buttons={[
                            {
                                color: 'cancel',
                                clickHandler: cancelFileUpload,
                                children: intl.formatMessage({ id: 'GLOBAL_Cancel' }),
                                dataCy: 'inputFile-modal-cancel-btn',
                            },
                            {
                                color: 'valid',
                                clickHandler: uploadFiles,
                                disabled: files.length === 0 && driveFiles.length === 0,
                                loading: isFilePopupLoading,
                                children: intl.formatMessage({ id: 'GLOBAL_Add' }),
                                dataCy: 'inputFile-modal-add-btn',
                            },
                        ]}>
                        <TabsWrapper>
                            <JRCTabs<FileTab>
                                direction={'horizontal'}
                                callback={(i) => {
                                    setActiveFileTabItem(i);
                                    deleteFiles();
                                }}
                                entries={[
                                    { label: intl.formatMessage({ id: 'GLOBAL_Import' }), key: 'upload' },
                                    ...(props.activeDrives?.map((d) => ({
                                        key: d,
                                        label: tabsName[d],
                                    })) ?? []),
                                ]}
                                activeItem={activeFileTabItem}
                            />
                        </TabsWrapper>
                        {activeFileTabItem === 'upload' && (
                            <JRCInputFileAdvancedRaw
                                onUploadStart={() => setFilePopupLoading(true)}
                                onUploadSuccess={(file) => {
                                    setFilePopupLoading(false);
                                    setFiles((prevFiles) => [...prevFiles, file]);
                                }}
                                token={token}
                                multiple={multiple}
                                deleteFile={async (file) => {
                                    setFiles((prevFiles) => prevFiles.filter((_, i) => file !== i));
                                    if (deleteFile) {
                                        await deleteFile(file);
                                    }
                                }}
                                id={'inner-input-file'}
                                dispatchOnError={props.dispatchOnError}
                            />
                        )}
                        {activeFileTabItem === 'fileBank' && props.handlers && (
                            <JRCFolders
                                handlers={props.handlers}
                                onSelectDocument={(f) => setFiles(f)}
                                loadDocuments
                                allowMultiSelection
                            />
                        )}
                        {activeFileTabItem === 'drive_microsoft_graph' && props.handlers && (
                            <JRCDriveMSGraph
                                useDocumentFilters={[]}
                                onSelectMultiDocument={setDriveFiles}
                                onError={props.dispatchOnError}
                                handlers={props.handlers}
                                handleLogin
                                onLogin={setLoggedAccount}
                                onLogout={() => setLoggedAccount(undefined)}
                            />
                        )}
                        {activeFileTabItem === 'drive_dropbox' && props.handlers && (
                            <JRCDriveDropbox
                                useDocumentFilters={[]}
                                onSelectMultiDocument={setDriveFiles}
                                onError={props.dispatchOnError}
                                handlers={props.handlers}
                                handleLogin
                                onLogin={setLoggedAccount}
                                onLogout={() => setLoggedAccount(undefined)}
                            />
                        )}
                        {activeFileTabItem === 'drive_google' && (
                            <JRCMessage
                                variant={'info'}
                                message={intl.formatMessage({ id: 'DRIVE_Google_Not_Available' })}
                            />
                        )}
                    </JRCModalContent>
                </JRCModalLayout>
            )}
        </>
    );
};

export const JRCInputFileAdvancedRaw = React.forwardRef(InputFileAdvancedRaw);
